import React, { useState, useContext, useRef } from 'react';
import { UserContext } from "../context";
import { ColorModeContext } from "../theme";
import {
    AppBar,
    Toolbar,
    Box,
    useTheme,
    Menu,
    MenuItem,
    Button,
    ListItemIcon,
    IconButton,
    Drawer,
    ListItemButton,
    List,
    ListItemText,
    Collapse,
    ListItem,
    ToggleButtonGroup,
    ToggleButton,
    Badge,
} from '@mui/material';
import { styled } from '@mui/system';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
    AccountTree,
    MenuOpen,
    Close,
    DarkMode,
    EmojiEvents,
    EventAvailable,
    ExpandLess,
    ExpandMore,
    Festival,
    Home,
    LightMode,
    Logout,
    People,
    ReportProblem,
    Search,
    VideoCameraFront,
} from '@mui/icons-material';
import LogoSection from './LogoSection';
import FreecomersBtn from './FreecomersBtn';
import WhatsAppCommunity from './WhatsAppCommunity';
import AccountSetting from './AccountSetting';
import { userLogout } from '../services/AuthApi';
import { removeToken, clearSessionData } from "../utils/tokenUtils";
import Bottombar from './Bottombar';
import BecomeMentor from '../pages/mentorship/BecomeMentor';
import NotificationMenu from '../pages/notification_page/NotificationMenu';
import Messages from '../pages/messages/Messages';


const Offset = styled('div')(({ theme }) => theme.mixins.toolbar);


// Navigation items configuration
const navItem = [
    { title: "Home", url: "home", icon: <Home sx={{ fontSize: "20px" }} /> },
    {
        title: "Network",
        icon: <People sx={{ fontSize: "20px" }} />,
        children: [
            { title: "People", url: "people", icon: <People sx={{ fontSize: "20px" }} /> },
            { title: "Jobs", url: "jobs", icon: <VideoCameraFront sx={{ fontSize: "20px" }} /> },
            { title: "Projects", url: "projects", icon: <AccountTree sx={{ fontSize: "20px" }} /> },
            { title: "Competitions", url: "competitions", icon: <EmojiEvents sx={{ fontSize: "20px" }} /> },
        ],
    },
    { title: "Filmy Friday", url: "events", icon: <EventAvailable sx={{ fontSize: "20px" }} /> },
    { title: "Festivals", url: "festivals", icon: <Festival sx={{ fontSize: "20px" }} />, soon: true },
];

export default function Navbar() {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, setUser, refreshUser } = useContext(UserContext);
    const { toggleColorMode } = useContext(ColorModeContext);
    const [mobileOpen, setMobileOpen] = useState(false);
    const pendingNavRef = useRef(null);
    const pendingMenuNavRef = useRef(null);

    const [menuState, setMenuState] = useState({
        anchorEl: null,
        index: null,
    });

    const handleOpenMenu = (event, index) => {
        setMenuState({
            anchorEl: event.currentTarget,
            index,
        });
    };

    const handleCloseMenu = () => {
        setMenuState({
            anchorEl: null,
            index: null,
        });
    };

    const handleMenuExited = () => {
        if (pendingMenuNavRef.current) {
            navigate(pendingMenuNavRef.current);
            pendingMenuNavRef.current = null;
        }
    };

    const [collapseIndex, setCollapseIndex] = useState(null);

    const handleMobileNav = (url) => {
        pendingNavRef.current = url;
        setMobileOpen(false);
    };

    const handleDrawerExited = () => {
        if (pendingNavRef.current) {
            navigate(pendingNavRef.current);
            pendingNavRef.current = null;
        }
    };

    const handleOpenCollapse = (index) => {
        setCollapseIndex(collapseIndex === index ? null : index);
    };


    const handleSignout = () => {
        console.log("Logout");

        // GTM tracking for logout
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: "logout",
            userId: user?.id,
            accountType: user?.account_type,
        });

        userLogout()
            .then((response) => {
                console.log(response);
                removeToken();
                clearSessionData();
                setMobileOpen(false);
                navigate("/", { replace: true });
                window.location.reload();
            })
            .catch((error) => {
                console.error(error);
            });
    };

    const isActive = (url) => location.pathname.includes(url);

    return (
        <>
            <AppBar
                position="fixed"
                enableColorOnDark
                elevation={0}
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: theme.palette.mode === "light" ? "0 1px 3px rgba(0,0,0,0.1)" : "0 1px 3px rgba(255,255,255,0.1)",
                }}
            >
                <Toolbar sx={{ display: "flex", gap: 2, justifyContent: "space-between", px: { xs: 1, md: 3 } }}>

                    <LogoSection user={user} />

                    <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 1 }}>
                        {navItem.map((item, index) => {
                            const isDropdown = !!item.children;
                            const active =
                                item.url
                                    ? isActive(item.url)
                                    : item.children?.some((c) => isActive(c.url));

                            return (
                                <Box key={index}>
                                    <Button
                                        onClick={(e) => {
                                            if (isDropdown) {
                                                handleOpenMenu(e, index);
                                            } else {
                                                navigate(item.url);
                                            }
                                        }}
                                        sx={{
                                            display: "flex",
                                            alignItems: "end",
                                            textTransform: "none",
                                            color: active
                                                ? theme.palette.primary.dark
                                                : theme.palette.text.secondary,
                                            "& .MuiButton-endIcon": {
                                                marginLeft: 0,
                                            }
                                        }}
                                        endIcon={
                                            isDropdown ?
                                                <ExpandMore fontSize='small' /> : null
                                        }
                                    >
                                        <Badge
                                            invisible={!item.soon}
                                            badgeContent="SOON"
                                            color='error'
                                            sx={{
                                                "& .MuiBadge-badge":
                                                    { fontSize: "0.5rem", height: "14px", minWidth: "14px" }
                                            }}
                                        >
                                            <Box
                                                component="span"
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    flexDirection: "column",
                                                    gap: 0.5,
                                                }}>

                                                {item.icon}
                                                {item.title}
                                            </Box>
                                        </Badge>
                                    </Button>


                                    {/* Dropdown */}
                                    {isDropdown && (
                                        <Menu
                                            anchorEl={menuState.anchorEl}
                                            open={menuState.index === index}
                                            onClose={handleCloseMenu}
                                            TransitionProps={{ onExited: handleMenuExited }}
                                            slotProps={{
                                                paper: {
                                                    elevation: 0,
                                                    sx: {
                                                        overflow: 'visible',
                                                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                                        borderRadius: 4,
                                                        mt: 1.5,
                                                        '&::before': {
                                                            content: '""',
                                                            display: 'block',
                                                            position: 'absolute',
                                                            top: 0,
                                                            right: 14,
                                                            width: 10,
                                                            height: 10,
                                                            bgcolor: 'background.paper',
                                                            transform: 'translateY(-50%) rotate(45deg)',
                                                            zIndex: 0,
                                                        },
                                                    },
                                                },
                                            }}
                                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                        >
                                            {item.children.map((child, i) => (
                                                <MenuItem key={i}
                                                    onClick={() => {
                                                        pendingMenuNavRef.current = child.url;
                                                        handleCloseMenu();
                                                    }}
                                                    selected={isActive(child.url)}>
                                                    <ListItemIcon color='text.secondary' sx={{ minWidth: 0 }}>
                                                        {child.icon}
                                                    </ListItemIcon>
                                                    <Box component="span" sx={{ fontSize: "0.9rem", color: theme.palette.text.secondary }}>
                                                        {child.title}
                                                    </Box>
                                                </MenuItem>

                                            ))}
                                        </Menu>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>

                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <IconButton
                            onClick={toggleColorMode}
                            sx={{
                                display: { xs: "none", sm: "flex" },
                                backgroundColor: theme.palette.action.hover,
                            }}
                        >
                            {theme.palette.mode === 'dark' ? <LightMode /> : <DarkMode />}
                        </IconButton>

                        {!user && (
                            <>
                                <Box sx={{ display: { xs: "none", md: "flex" } }}>
                                    <FreecomersBtn size="small" mode="beigeish" btntxt="Sign In" onClick={() => navigate("/signin")} />
                                </Box>
                                <FreecomersBtn size="small" mode="redish" btntxt="Join Free" onClick={() => navigate("/signup")} />
                            </>
                        )}

                        {user && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <WhatsAppCommunity header={true} />
                                <IconButton sx={{ display: { xs: "none", md: "flex" }, backgroundColor: theme.palette.action.hover }}
                                    onClick={() => navigate("/search")}
                                >
                                    <Search />
                                </IconButton>

                                <NotificationMenu user={user} />

                                <Messages bottombar={false} />

                                <AccountSetting user={user} setUser={setUser} refreshUser={refreshUser} navigate={navigate} setMobileOpen={setMobileOpen} />
                            </Box>
                        )}


                        <IconButton sx={{ display: { xs: "flex", sm: "none" }, backgroundColor: theme.palette.action.hover }} onClick={() => setMobileOpen(true)}>
                            <MenuOpen />
                        </IconButton>
                    </Box>

                </Toolbar>
            </AppBar>
            <Offset />




            <Drawer
                anchor="right"
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                SlideProps={{ onExited: handleDrawerExited }}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: 250,
                        boxSizing: 'border-box',
                    },
                }}
            >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 1 }}>
                    <Box
                        component={"img"}
                        src="/assets/logo-red-(512x110).png"
                        alt="logo"
                        sx={{ width: "51%", height: "auto", ml: 1 }}
                    />

                    <IconButton onClick={() => setMobileOpen(false)}>
                        <Close />
                    </IconButton>
                </Box>

                {/* Mobile menu content can go here */}
                <List>
                    {navItem.map((item, index) => {
                        const isDropdown = !!item.children;

                        const active =
                            item.url
                                ? isActive(item.url)
                                : item.children?.some((c) => isActive(c.url));

                        const isOpen = collapseIndex === index;

                        return (
                            <Box key={index}>
                                {/* Parent Item */}
                                <ListItemButton
                                    selected={active}
                                    onClick={() => {
                                        if (isDropdown) {
                                            handleOpenCollapse(index);
                                        } else {
                                            handleMobileNav(item.url);
                                        }
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: "40px" }}>
                                        {item.icon}
                                    </ListItemIcon>

                                    <ListItemText primary={
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            {item.title}
                                            {item.soon && (
                                                <Box component="span" sx={{
                                                    fontSize: "0.6rem",
                                                    color: "#fcfcfc",
                                                    backgroundColor: "error.main",
                                                    borderRadius: "50px",
                                                    px: 0.5,
                                                    py: 0.1,
                                                    lineHeight: 1.4,
                                                }}>SOON</Box>
                                            )}
                                        </Box>
                                    } />

                                    {/* Expand Icon */}
                                    {isDropdown &&
                                        (isOpen ? <ExpandLess /> : <ExpandMore />)}
                                </ListItemButton>

                                {/* Collapse Children */}
                                {isDropdown && (
                                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                        <List component="div" disablePadding>
                                            {item.children.map((child, i) => (
                                                <ListItemButton
                                                    key={i}
                                                    sx={{ pl: 4 }}
                                                    selected={isActive(child.url)}
                                                    onClick={() => handleMobileNav(child.url)}
                                                >
                                                    <ListItemIcon sx={{ minWidth: "40px" }}>
                                                        {child.icon}
                                                    </ListItemIcon>

                                                    <ListItemText primary={child.title} />
                                                </ListItemButton>
                                            ))}
                                        </List>
                                    </Collapse>
                                )}
                            </Box>
                        );
                    })}
                    {user && (
                        <ListItemButton>
                            <ListItemIcon sx={{ minWidth: "40px" }}>
                                <ReportProblem />
                            </ListItemIcon>
                            <ListItemText primary="Report Issue" />
                        </ListItemButton>
                    )}
                </List>

                <Box sx={{ padding: 2 }}>
                    <BecomeMentor isSidebar={true} />
                </Box>

                <Box sx={{ mt: "auto" }}>
                    <List disablePadding>
                        <ListItem disableGutters disablePadding>
                            <ToggleButtonGroup
                                size="small"
                                value={theme.palette.mode}
                                exclusive
                                onChange={(e, mode) => {
                                    if (mode) toggleColorMode();
                                }}
                                fullWidth
                            >
                                <ToggleButton value="light" disabled={theme.palette.mode === "light"}>
                                    <LightMode sx={{ mr: 1 }} /> Light
                                </ToggleButton>
                                <ToggleButton value="dark" disabled={theme.palette.mode === "dark"}>
                                    <DarkMode sx={{ mr: 1 }} /> Dark
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </ListItem>

                        {user && (
                            <ListItem disableGutters disablePadding>
                                <ListItemButton onClick={handleSignout}>
                                    <ListItemIcon sx={{ minWidth: "40px" }}>
                                        <Logout />
                                    </ListItemIcon>
                                    <ListItemText primary="Signout" />
                                </ListItemButton>
                            </ListItem>
                        )}
                    </List>
                </Box>

            </Drawer>

            {user && <Bottombar user={user} />}
            <Box component="main" sx={{ flexGrow: 1, pt: 1 }}>
                <Outlet />
            </Box>
        </>
    );
}